import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { arrayMove } from '@dnd-kit/sortable';
import { useWebsocket } from '@lanetalk/websocket-sdk/react';
import { generatePath, useNavigate } from 'react-router-dom';

import routesPath from '@/shared/constants/routesPath';
import useBowlingCentersStore from '@/shared/store/bowlingCenters';
import useSettingsStore from '@/shared/store/settings';
import useUserStore from '@/shared/store/user';
import { getPlayerId } from '@/shared/utils';

import { getBowlingCenter } from '@/services/home';

const FEAT_USER_LIVE_SCORING = 'bowlingcenter.liveGames';
const BREAKPOINTS = [
  { width: 640, base: 1, maxColumns: 1 },
  { width: 1024, base: 2, maxColumns: 2 },
  { width: 1200, base: 3, maxColumns: 5 },
  { width: 1440, base: 3, maxColumns: 6 },
  { width: 1920, base: 3, maxColumns: 8 },
];

const LANE_HEIGHT = 150;
const PADDING_LEFT_RIGHT = 32; // Total padding left and right
const GAP = 16; // Gap between columns
const FINISHED_GAMES_TIMEOUT = import.meta.env.DEV ? 1 : 2; // In minutes
const RECONNECT_THRESHOLD = import.meta.env.DEV ? 1 : 10; // In minutes

const getBreakpointSettings = () => {
  const viewportWidth = window.innerWidth;

  for (const breakpoint of BREAKPOINTS) {
    const { width, base, maxColumns } = breakpoint;
    if (viewportWidth < width) {
      return {
        base,
        maxColumns,
      };
    }
  }
  // default settings for ultra-wide screens, starting from 1920
  return {
    base: 3,
    maxColumns: 10,
  };
};

const isGameFinished = (throws) => {
  if (throws.length === 21) {
    return true;
  }

  if (throws.length === 20) {
    return !(throws[18] === 'X' || throws[19] === 'X' || throws[19] === '/');
  }

  return false;
};

export default function useLive() {
  const navigate = useNavigate();
  const { current: bc, addCurrentCenter } = useBowlingCentersStore();

  const settings = useSettingsStore();
  const { user } = useUserStore();

  // Check if user has access to live scoring
  // Access is granted if:
  // 1. Center has liveScores feature enabled (available to all users), OR
  // 2. User is logged in and has livescoring in their activeFeatures
  const hasAccess = useMemo(() => {
    const centerHasFeature = bc?.features?.liveScores === true;
    const userHasFeature = user?.activeFeatures?.includes(
      FEAT_USER_LIVE_SCORING
    );

    return Boolean(centerHasFeature || userHasFeature);
  }, [bc?.features?.liveScores, user?.activeFeatures]);

  const wsRef = useRef();
  const hasRequestedFeaturesRef = useRef(false);

  const { isConnected, subscriptionData, lastMessage, error } = useWebsocket({
    url: import.meta.env.VITE_WEBSOCKET_URL,
    channel: `LiveScores:${bc?.uuid}`,
    apiKey: import.meta.env.VITE_WEBSOCKET_TOKEN,
    debug: !!import.meta.env.DEV,
    ref: wsRef,
    enabled: hasAccess, // Only connect if access is granted
  });

  const [followPlayers, setFollowPlayers] = useState([]);
  const [lanes, setLanes] = useState(new Map());
  const [baseColumns, setBaseColumns] = useState(3);

  const [gridRowsHeight, setGridRowsHeight] = useState(null);
  const listRef = useRef(null);
  const timeoutIdsRef = useRef(new Map());
  const lastMessageTimeRef = useRef();

  /** EFFECTS */
  const shouldRefreshCenterFeatures =
    Boolean(bc?.uuid) &&
    (!bc?.features || Object.keys(bc.features).length === 0);

  useEffect(() => {
    if (!shouldRefreshCenterFeatures || hasRequestedFeaturesRef.current) return;

    let isMounted = true;
    hasRequestedFeaturesRef.current = true;

    const fetchCenter = async () => {
      try {
        const response = await getBowlingCenter(bc.uuid);
        const centerData = response?.data?.data ?? response?.data;

        if (isMounted && centerData) {
          addCurrentCenter(centerData);
        }
      } catch (fetchError) {
        console.error('Failed to refresh bowling center data.', fetchError);
        if (isMounted) {
          hasRequestedFeaturesRef.current = false;
        }
      }
    };

    fetchCenter();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefreshCenterFeatures, bc?.uuid]);

  useEffect(() => {
    const centerData = subscriptionData?.publications?.find(
      (publication) => publication.type === 2
    );

    if (centerData?.data) {
      const processedLanes = new Map();
      // Initialize all lanes with an empty array to accumulate players
      for (let i = 1; i <= bc.lanes; i++) {
        processedLanes.set(i, []);
      }

      centerData.data.lanes.forEach((playerInfo) => {
        if (processedLanes.has(playerInfo.lane)) {
          processedLanes
            .get(playerInfo.lane)
            .push({ ...playerInfo, id: getPlayerId(playerInfo) });
        }
      });
      setLanes(processedLanes);

      // Check if hideInactiveLanes is true and update active lanes
      if (settings.hideInactiveLanes) {
        const activeLanes = Array.from(processedLanes)
          .filter(([, players]) => players.length > 0)
          .map(([laneNumber]) => laneNumber);
        updateSettings({
          lanes: activeLanes,
        });
      } else {
        // if settings.lanes is not empty, and if some of the lanes are not in the processedLanes, then update the lanes
        if (
          settings.lanes.length > 0 &&
          !settings.lanes.every((lane) => processedLanes.has(lane))
        ) {
          updateSettings({
            lanes: [],
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData]);

  useEffect(() => {
    if (lastMessage?.type === 5) {
      const updatedLanesMap = new Map(lanes);
      const laneToUpdate = lastMessage.data.lane;
      const playerToUpdateId = getPlayerId(lastMessage.data);

      let playersInLane = updatedLanesMap.get(laneToUpdate) || [];
      const playerIndex = playersInLane.findIndex(
        (p) => p.id === playerToUpdateId
      );

      if (playerIndex !== -1) {
        playersInLane = playersInLane.map((player, index) =>
          index === playerIndex ? { ...player, ...lastMessage.data } : player
        );
      } else {
        playersInLane = [
          ...playersInLane,
          { ...lastMessage.data, id: playerToUpdateId },
        ];
      }

      updatedLanesMap.set(laneToUpdate, playersInLane);
      setLanes(updatedLanesMap);

      const currentPlayersInLane = updatedLanesMap.get(laneToUpdate) || [];
      const lastPlayer = currentPlayersInLane[currentPlayersInLane.length - 1];
      const firstPlayer = currentPlayersInLane[0];

      const shouldClearGames =
        lastPlayer &&
        isGameFinished(lastPlayer.throws) &&
        firstPlayer &&
        isGameFinished(firstPlayer.throws);

      if (shouldClearGames) {
        console.debug('Finished games in lane', laneToUpdate);

        // Clear any existing timeout for this lane
        if (timeoutIdsRef.current.has(laneToUpdate)) {
          clearTimeout(timeoutIdsRef.current.get(laneToUpdate));
        }

        const timeoutId = setTimeout(
          () => {
            filterFinishedGames(laneToUpdate, settings, onFilterLanes);
            timeoutIdsRef.current.delete(laneToUpdate);
          },
          FINISHED_GAMES_TIMEOUT * 60 * 1000
        );
        timeoutIdsRef.current.set(laneToUpdate, timeoutId);
      }

      // update the last message time
      lastMessageTimeRef.current = Date.now();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  // Side effect to update the list of followed players
  useEffect(() => {
    if (settings.followPlayers.length > 0 && lanes.size > 0) {
      const currentPlayers = Array.from(lanes.values()).flatMap(
        (player) => player
      );
      const playersToFollow = currentPlayers.filter((player) =>
        settings.followPlayers.includes(getPlayerId(player))
      );

      setFollowPlayers(playersToFollow);

      // If no players match, update the settings.followPlayers to an empty array
      if (playersToFollow.length === 0) {
        updateSettings({ followPlayers: [] });
      }
    } else if (
      settings.followPlayers.length === 0 &&
      followPlayers.length !== 0
    ) {
      setFollowPlayers([]); // Reset followPlayers only if it's not already empty
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, settings.followPlayers]);

  // Side effect to update the grid template rows
  useEffect(() => {
    if (listRef.current && lanes.size > 0) {
      const rows = Array.from(listRef.current.children);
      const heights = [];
      const columns = settings.columns || baseColumns;
      let currentRow = [];
      let maxHeight = 0;

      rows.forEach((row, index) => {
        const rowHeight = row.firstElementChild.getBoundingClientRect().height;
        maxHeight = Math.max(maxHeight, rowHeight);
        currentRow.push(row);

        if ((index + 1) % columns === 0 || index === rows.length - 1) {
          heights.push(maxHeight);
          currentRow = [];
          maxHeight = 0;
        }
      });

      const filteredZeroHeights = heights
        .map(
          (height) =>
            height < LANE_HEIGHT ? `${LANE_HEIGHT}px` : `${height}px` // 84 = min heigth for a single player
        )
        .join(' ');
      setGridRowsHeight(filteredZeroHeights);
    }
  }, [settings.columns, settings.lanes, baseColumns, lanes]);

  // Side effect to update the base columns
  // and reconnect on document focus
  useEffect(() => {
    const handleResize = () => {
      const { base, maxColumns } = getBreakpointSettings();
      setBaseColumns(base);
      updateSettings({
        columns: settings.columns > maxColumns ? base : settings.columns,
        maxColumns,
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeSinceLastMessage = now - lastMessageTimeRef.current;
        const threshold = Date.now() + RECONNECT_THRESHOLD * 60 * 1000;

        if (timeSinceLastMessage > threshold) {
          // Reconnect WebSocket or refresh data
          console.debug('Tab regained focus, reconnecting WebSocket...');
          wsRef.current.connection.disconnect();
          wsRef.current.connection.connect();
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean the timeouts when the component unmounts
  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current;
    return () => {
      timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutIds.clear();
      console.debug('Cleared all timeouts');
    };
  }, []);
  /** END EFFECTS */

  /** METHODS */

  const updateSettings = useCallback(
    (newValues) => {
      Object.entries(newValues).forEach(([key, value]) => {
        settings.onUpdateSettings(key, value);
      });
    },
    [settings]
  );

  const onHideInactiveLanes = () => {
    let activeLanes = [];

    if (!settings.hideInactiveLanes) {
      activeLanes = [...lanes]
        .filter(([, players]) => players.length > 0)
        .map(([laneNumber]) => laneNumber);
    }

    updateSettings({
      hideInactiveLanes: !settings.hideInactiveLanes,
      lanes: activeLanes,
    });
  };

  const onFilterLanes = useCallback(
    (lane) => {
      if (settings.lanes.includes(lane)) {
        updateSettings({
          lanes: settings.lanes.filter((l) => l !== lane),
        });
      } else {
        updateSettings({
          lanes: [...settings.lanes, lane],
          hideInactiveLanes: false,
        });
      }
    },
    [updateSettings, settings.lanes]
  );

  const filterFinishedGames = useCallback(
    (laneNumber, currentSettingsFromRef, currentOnFilterLanes) => {
      setLanes((prevLanes) => {
        const updatedLanes = new Map(prevLanes);
        const laneData = updatedLanes.get(laneNumber);
        if (!laneData) return prevLanes;

        const activePlayers = laneData.filter(
          (player) => !isGameFinished(player.throws)
        );
        updatedLanes.set(laneNumber, activePlayers);

        if (activePlayers.length === 0) {
          console.debug('Cleared finished games in lane', laneNumber);
          if (currentSettingsFromRef.hideInactiveLanes) {
            currentOnFilterLanes(laneNumber);
          }
        }

        return updatedLanes;
      });
    },
    []
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    if (active?.id !== over?.id) {
      const oldIndex = followPlayers.findIndex(
        (player) => player.id === active.id
      );
      const newIndex = followPlayers.findIndex(
        (player) => player.id === over.id
      );
      const newFollowPlayers = arrayMove(followPlayers, oldIndex, newIndex);
      setFollowPlayers(newFollowPlayers);
    }
  };
  const onSeeFinishedGames = () => {
    const currentPath = generatePath(routesPath.LIVESCORING, {
      uuid: bc?.uuid || '',
    });
    navigate(`${currentPath}?tab=finished`);
  };
  /** END METHODS */

  // Calculate the scale factor based on the number of columns
  const scaleFactor = useMemo(() => {
    if (settings.columns === 1 && window.innerWidth < 640) {
      const smallScreenScaleFactor = window.innerWidth / 640;
      return smallScreenScaleFactor < 1 ? smallScreenScaleFactor : 1;
    }

    return settings.columns
      ? (baseColumns / settings.columns) * (window.innerWidth / 1920)
      : 1;
  }, [settings.columns, baseColumns]);

  const laneWidth =
    (window.innerWidth - PADDING_LEFT_RIGHT - GAP * (settings.columns - 1)) /
    settings.columns /
    scaleFactor;

  const sortedLanes = lanes
    ? Array.from(lanes.entries()).sort(
        // eslint-disable-next-line no-unused-vars
        ([a_key, players], [b_key, otherPlayers]) => {
          const firstPlayerLane = players[0]?.lane;
          const otherFirstPlayerLane = otherPlayers[0]?.lane;
          return firstPlayerLane - otherFirstPlayerLane;
        }
      )
    : [];

  // Final access check: combine pre-check with WebSocket error response
  // Access is denied if either:
  // 1. Pre-check failed (no center feature AND no user feature), OR
  // 2. WebSocket returned unauthorized error (code 101)
  const isAccessRestricted = !hasAccess || error?.code === 101;

  // Only show loading when:
  // - WebSocket is not connected yet, AND
  // - Access is not restricted (we're actually trying to connect)
  const isLoading = !isConnected && !isAccessRestricted;

  return {
    isLoading,
    isAccessRestricted,
    lanes: sortedLanes,
    settings: {
      columns: settings.columns,
      lanes: settings.lanes,
      showPinLeaves: settings.showPinLeaves,
      listRef,
      gridTemplateRows: gridRowsHeight,
      scaleFactor,
      laneWidth,
      baseColumns,
      gap: GAP,
    },
    hasNoGames: Array.from(lanes.values()).every(
      (players) => players.length === 0
    ),
    hasError: lastMessage?.error && error?.code !== 101,
    followPlayers,
    onHideInactiveLanes,
    onFilterLanes,
    onDragEnd,
    onSeeFinishedGames,
  };
}
